/**
 * Logging SSOTs. LOGGER_TAGS = scopedLogger module-name argument. LOG_EVENTS = structured-log `event:` field literal
 * (dashboards key off it, so a rename is a cross-system migration). LOG_LEVELS = serialized JSON-log discriminator; its
 * contract is `.error` = unhandled/invariant/action-required, `.warn` = caught-and-degraded, and level demotions ride Phase-4 conversions, NEVER a standalone relevel wave. SCAN_OUTPUT_SEVERITY = linter / scanner `severity:` field.
 */

export const LOGGER_TAGS = {
	ACTIVITY_TRACKING: 'activity-tracking',
	ADMIN_API: 'AdminAPI',
	APP_SESSION: 'app-session',
	APP_STATE: 'AppState',
	ASSISTANT_CONTEXT: 'AssistantContext',
	BID_FORM: 'bid-form',
	CACHE_HELPERS: 'cache-helpers',
	CHANNEL_RESPONSE: 'channel-response',
	CM_GENERATE: 'cm-generate',
	CM_JUNCTION: 'cm-junction',
	COMPLIANCE_HELPERS: 'compliance-helpers',
	COMPLIANCE_SCANNER_DISPATCH: 'compliance-scanner-dispatch',
	COUNTERMEASURE_JUNCTION: 'countermeasure-junction',
	COUNTERMEASURE_SINGLE: 'countermeasure-single',
	EMBEDDED_CHECKOUT: 'EmbeddedCheckout',
	EXPLORE: 'Explore',
	EXPLORE_CLIENT: 'ExploreClient',
	EXPLORE_WEBSOCKET_PROVIDER: 'ExploreWebSocketProvider',
	FETCH_FROM_HTTP: 'fetchFromHttp',
	FETCH_PROPERTIES: 'fetchProperties',
	FETCH_RECENT_MESSAGES: 'fetchRecentMessages',
	GET_RUNTIME_CONFIG_SAFE: 'getRuntimeConfigSafe',
	MESSAGING_CLIENT: 'MessagingClient',
	NOTIFICATIONS_PAGE: 'NotificationsPage',
	OFFERS_PAGE: 'OffersPage',
	OG_IMAGE: 'OG Image',
	PROPERTY_DETAIL_CLIENT: 'PropertyDetailClient',
	PROPERTY_DETAIL_PROVIDER: 'PropertyDetailProvider',
	/** The PropertyDO's own log SOURCE inside the PROPERTY_OPS scope: the property flows it runs in-process log under it. */
	PROPERTY_DO: 'PropertyDo',
	PROPERTY_OPS: 'PropertyOps',
	RCA_ANALYSIS: 'rca-analysis',
	RCA_ORCHESTRATOR: 'rca-orchestrator',
	REDUX: 'Redux',
	RESOLVE_MESSAGE: 'resolveMessage',
	SAFE_AREA_PROVIDER: 'SafeAreaProvider',
	SCAN_LOCK: 'scan-lock',
	/** The single sign-on hub's own server routes (`apps/ifederated/src/lib/`). */
	SSO_HUB_SESSION: 'sso-hub-session',
	SITEMAP: 'sitemap',
	TELEMETRY: 'Telemetry',
	USE_REFUND_HANDLER: 'useRefundHandler',
	WISHLISTS_PAGE: 'WishlistsPage',
	TWILIO_INBOUND: 'twilio-inbound',
	AI_TELEMETRY_INGEST: 'ai-telemetry-ingest',
	/** The cross-account telemetry relay worker's isolate-guard scope and service name. */
	TELEMETRY_RELAY: 'telemetry-relay',
	/** The assistant tool door worker's isolate-guard scope and service name. */
	FEDERA_AI_TOOLS: 'federa-ai-tools',
	COMPLIANCE_SCAN: 'compliance-scan',
	MARK_COMPLETED_TARGET_FILES_VERIFY: 'mark-completed-target-files-verify',
	OPENAI_AGENTS: 'openai-agents',
	QUEUE_AGENT_DISPATCH: 'queue-agent-dispatch',
	RCA_AGENT_FACTORY: 'rca-agent-factory',
	/** The FlowRunner worker entry's isolate-guard scope and service name. */
	FLOW_RUNNER: 'flow-runner',
} as const;

export const LOG_EVENTS = {
	COMPONENT_ERROR: 'component_error',
	// NO FLOW-OUTCOME EVENT: every fact a run summary line would carry is a field the platform stamps on the run's own
	// span, or one the canonical `FlowRunRecord` carries to the journal and the door. Do not mint a member for a
	// hand-rolled run summary.
	LEDGER_COUNTERPARTY_LINK_FAILED_NON_CRITICAL: 'ledger.counterparty-link.failed-non-critical',
	AUCTION_UPDATE_TRANSACTIONS_FAILED: 'auction-update-transactions.failed',
	AUTH_TOKEN_VERIFICATION_FAILED: 'verifyFirebaseTokenStep: token verification failed:',
	MARKET_MAKER_STATUS_CHANGE_FAILED: 'market-maker.status-change.failed',
	MARKET_MAKER_STATUS_LOOKUP_FAILED: 'D1 lookup failed for market maker',
	PLACES_AUTOCOMPLETE_REQUEST_FAILED: 'Google Places autocomplete request failed',
	SYNC_AGGREGATE_TO_DO_FAILED: 'sync-aggregate-to-do.failed',
	/** `sendToUserDO` REJECTED, which its contract says cannot happen -- every ordinary miss (absent binding, absent id,
	 *  non-OK DO answer) arrives as a `{ status, message }` envelope the step reads instead. Reaching this event is
	 *  therefore the news itself: the transport or the stub broke, not the sync. Emitted by `makeUserDoSyncStep`
	 *  (`utils/users/user-do-sync-step.ts`) through its injected reporter; the D1 write is already committed by then, so
	 *  the run still succeeds and this line is the only place the throw is observable. */
	USER_DO_SYNC_SEND_THREW: 'user-do-sync.send-threw',
	TAIL_BATCH_IDLE: 'tail_batch_idle',
	TITLE_RUN_FINDINGS_PRUNE_READDIR_FAILED: 'title.run-findings.prune-readdir-failed',
	TAIL_BATCH_SUMMARY: 'tail_batch_summary',
	USER_WEBSOCKET_ERROR: 'user.websocket.error',
	USER_WEBSOCKET_MESSAGE_ERROR: 'user.websocket.message_error',
	USER_WEBSOCKET_SETUP_FAILED: 'user.websocket.setup_failed',
	// Callsite event-label discriminators harvested to the SSOT so QUAL-SECURE-LOGGER-LABEL-SSOT-001 call sites reference them symbolically. Values are byte-identical to the originating literal so serialized log output is unchanged.
	/** The single sign-on key resolution's three refusals (`tools/auth/single-sign-on-keys.ts`): no service account bound, a bound value that is not a whole key file, and a key file for another Firebase project. */
	SSO_SERVICE_ACCOUNT_UNSET: 'single sign-on: FIREBASE_SERVICE_ACCOUNT is not set on this worker',
	SSO_SERVICE_ACCOUNT_INCOMPLETE: 'single sign-on: FIREBASE_SERVICE_ACCOUNT is not a complete service account key file',
	SSO_SERVICE_ACCOUNT_FOREIGN_PROJECT: 'single sign-on: FIREBASE_SERVICE_ACCOUNT belongs to another Firebase project',
	ASSISTANT_PANE_ATTACHMENTS_UPLOADING: 'AssistantPane: uploading attachments via same-origin route',
	ASSISTANT_PANE_CONVERSATIONS_FETCH_FAILED: 'Failed to fetch conversations',
	ASSISTANT_PANE_FILE_UPLOAD_FAILED: 'AssistantPane: File upload failed',
	ASSISTANT_PANE_MESSAGES_FETCH_FAILED: 'AssistantPane: Failed to fetch messages',
	/** The dead-letter arm could not file the kaizen issue for a dead-lettered assistant-queue message. Action-required: the arm retries a bounded number of times and Queues then DROPS the message, so a run of these is silent payload loss. */
	ASSISTANT_QUEUE_DEAD_LETTER_PERSIST_FAILED: '[assistant-worker] dead-letter persist failed',
	/** A sentence chunk could not be pushed to ConnectCallDO during a phone turn -- a transport reject OR a non-OK answer from the sink (a 404 on a renamed route, a 400 on a malformed body). The run continues by design, so this is the ONLY signal that a caller heard silence -- a run of these means the relay leg is broken, not the conversation. */
	ASSISTANT_VOICE_CHUNK_PUSH_FAILED: '[assistant-worker] voice chunk push failed',
	/** ConnectCallDO ACCEPTED the chunk request and discarded the chunk: no turn was in flight. This is the interrupt
	 *  reconciliation working -- every straggler from a turn the caller talked over lands here -- so it is INFO, not a
	 *  fault. Distinct from the push failure above precisely so a real refusal is not buried under barge-in traffic; a
	 *  run of these with NO interrupts on the call is the anomaly worth chasing. */
	ASSISTANT_VOICE_CHUNK_DROPPED: '[assistant-worker] voice chunk dropped -- no turn in flight',
	/** A PHONE turn ran with no chunk sink, so the caller heard silence for the whole turn while the run reported
	 *  success. Distinct from the push failure above: nothing failed, the sink was never built -- which is a binding or
	 *  admission problem, and the only place it is observable. */
	ASSISTANT_VOICE_TURN_NO_SINK: '[assistant-worker] phone turn ran with no voice chunk sink',
	/** The `group_phone_numbers` registration read for a PHONE turn could not answer, so the turn was narrowed to the
	 *  world corpus. The ROUTE is the designed fail-closed one and is unchanged; this event is the only thing that tells
	 *  a broken read apart from a number nobody registered, which the seed collapses into one arm on purpose. */
	ASSISTANT_VOICE_GROUP_SCOPE_LOOKUP_FAILED: '[assistant-worker] voice turn group-scope lookup failed -- answering from the world corpus',
	ASSISTANT_WORKER_REQUEST_FAILED: '[assistant-worker] request failed',
	CALL_FLOW_ROUTE_BODY_PARSE_FAILED: '[call-flow-route] Failed to parse request body',
	CHATROOM_TRIGGER_SAVE_MESSAGE_FLOW: 'ChatRoom.triggerSaveMessageFlow',
	CREATE_ISSUE_FN_HEARTBEAT_FAILED: 'createIssueFn failed (heartbeat error)',
	DESK_EXECUTOR_CLOSE_TICKET_FAILED: 'desk.executor.close-ticket-failed',
	DESK_EXECUTOR_ERROR_COMMENT_FAILED: 'desk.executor.error-comment-failed',
	DESK_EXECUTOR_SUMMARY_COMMENT_FAILED: 'desk.executor.summary-comment-failed',
	DESK_EXECUTOR_TAG_ERROR_FAILED: 'desk.executor.tag-error-failed',
	EXPLORE_PLACE_AUTOCOMPLETE_FETCH_FAILED: 'Failed to fetch place autocomplete',
	EXPLORE_PROPERTY_SHARE_FAILED: 'Property share failed',
	EXPLORE_SUGGESTION_PROPERTY_OPEN_FAILED: 'Failed to open property from suggestion',
	EXTRACT_USER_IDENTITY_MAPPING_FAILED: 'extractUserIdentity: firebase-to-db mapping failed',
	FAVORITE_PROPERTY_DETAILS_FETCH_FAILED: 'Failed to fetch favorite property details',
	FIREBASE_AUTH_TRACKING_ACCESS_FAILED: 'Failed to access Firebase auth for tracking identifiers',
	FLOW_ERROR_RESPONSE_REPORTED: '[flow-error-response] flow reported failure:',
	HEARTBEAT_TICK_ERROR: 'Heartbeat tick error',
	MISSING_OPTIONAL_BINDINGS: 'Missing optional bindings:',
	MUTATION_BODY_PARSE_FAILED: '[parseMutationBody] body parse/disconnect failure:',
	ROUTE_ERROR_RESPONSE_LOGGED: '[logged-error-response]',
	// BARE event text: `loggedRouteWarning` composes the leading `[transactions-detail]` tag from the surface its
	// structured `logPrefix` already carries, so the identity is spelled ONCE (at the route) and the rendered line is
	// byte-identical to the previously embedded form.
	TRANSACTIONS_DETAIL_OFFERS_QUERY_FAILED: 'offers query failed; returning empty offers',
	/** The messaging twin of `ASSISTANT_VOICE_GROUP_SCOPE_LOOKUP_FAILED`: the inbound-message graph's registration read
	 *  was REFUSED (the step runs `onFailure: 'continue'`), so the turn narrowed to the world corpus. The graph collapses
	 *  the refused read and the unregistered number into one ROUTING arm on purpose -- this event is the observability
	 *  half that collapse does not carry. */
	TWILIO_INBOUND_GROUP_SCOPE_LOOKUP_FAILED: 'twilio-inbound group-scope lookup failed -- answering from the world corpus',
	VALIDATION_ERROR_RESPONSE_REJECTED: '[validation-error-response] request rejected:',
	MISSING_REQUIRED_BINDINGS: 'Missing required bindings:',
	PROCESS_ERROR_PRE_EXIT_HOOK_FAILED: 'Process error pre-exit hook failed:',
	SCHEMA_SUPPLEMENT_PARSE_FAILED: 'schema-supplement scan: supplement JSON parse failed, skipping scan',
	SEO_SITE_ASSET_URL_DEFAULT_USED: 'Using default site asset URL',
	SEO_SITE_URL_RESOLVE_FAILED_DEFAULT_USED: 'Unable to resolve site URL, using default value',
	UNCAUGHT_EXCEPTION: 'Uncaught exception:',
	UNHANDLED_REJECTION: 'Unhandled rejection:',
	WORKTREE_HELPERS_BRANCH_DELETE_FAILED: 'worktree-helpers: branch-delete-failed',
	/** The kaizen INGEST DOOR's own run verdict. `runFlowDef` reports a failed run instead of throwing it, so without
	 *  this line a rejected claim gate, a failed INSERT and a completed write are byte-identical to every caller of
	 *  `flowBackedInsertIssue`. Logged through the PLAIN logger, never an issue-logging seam: reporting the ingest
	 *  door's failure back through the ingest door would recurse on the outage being reported. */
	KAIZEN_INGEST_ISSUE_CREATE_RUN_FAILED: 'flowBackedInsertIssue: issue-create run failed',
	// Browser-tier call sites harvested on the same terms as the block above: values byte-identical to the
	// originating literal, so rendered log output is unchanged and only the reference becomes symbolic.
	AUTH_SIGN_OUT_FAILED: 'Failed to sign out',
	EMBEDDED_CHECKOUT_DESTROY_FAILED: 'Error destroying embedded checkout',
	EMBEDDED_CHECKOUT_ERROR: 'Embedded checkout error',
	CLIPBOARD_COPY_FAILED: 'Failed to copy to clipboard',
	EXPLORE_SSR_PROPERTIES_PREFETCH_FAILED: 'Failed to prefetch explore properties for SSR',
	INLINE_PROPERTY_CARD_PROPERTY_FETCH_FAILED: 'InlinePropertyCard: property fetch failed',
	REFUND_ACTIVITY_TRACK_FAILED: 'Failed to track refund activity',
	SEO_DEFAULT_SITE_URL_INVALID_LOCALHOST_USED: 'Invalid default site URL, using localhost',
	TOP_UP_ANALYTICS_EVENT_RECORD_FAILED: 'Failed to record top up analytics event',
	TOP_UP_CHECKOUT_SESSION_CREATE_FAILED: 'Failed to create checkout session',
	TOP_UP_FUNDING_PROFILE_LOAD_FAILED: 'Failed to load funding profile',
	TOP_UP_ISSUE_LOG_FAILED: 'Failed to log top up issue',
	TOP_UP_SESSION_POLLING_FAILED: 'Session polling failed',
	// REGISTRY-LOGGER event labels, harvested on the same terms as the blocks above: values byte-identical to the
	// originating literal, so rendered log output is unchanged and only the reference becomes symbolic. These reach the
	// fleet sink through `logWarn` / `logError` (`tools/observability/activity-emit.ts`) rather than `secureLogger`, and
	// the SSOT obligation is the same one -- a dashboard keys off the discriminator regardless of which sink emitted it.
	// QUAL-REGISTRY-LOGGER-EVENT-LABEL-SSOT-001 is the forbid-rule pairing this block.
	/** The per-user NotificationChannelDO namespace binding is absent from the writer's env, so no live socket ever sees the change. A manifest defect the deploy could have caught -- D1 still holds the truth, so the badge is stale rather than wrong. */
	NOTIFICATIONS_BROADCAST_BINDING_ABSENT: 'notifications.broadcast.binding-absent',
	/** The DO answered the broadcast poke non-2xx (a renamed route, a malformed body). Best-effort by design; a run of these means the real-time lane is broken. */
	NOTIFICATIONS_BROADCAST_REJECTED: 'notifications.broadcast.rejected',
	/** The broadcast poke threw in transport. Composed onto the DO class name at the call site, so the published source reads `<DoClassName>.notifications.broadcast.failure`. */
	NOTIFICATIONS_BROADCAST_FAILURE: 'notifications.broadcast.failure',
	/** An outbid bidder's void notification run reported failure. Best-effort: the void itself already landed. */
	VOIDED_BIDDER_NOTIFICATION_FAILED: 'voided_bidder_notification_failed',
	/** The same notification dispatch THREW rather than reporting a failed run -- distinct from the arm above so a vanished run is not read as a refused one. */
	VOIDED_BIDDER_NOTIFICATION_THREW: 'voided_bidder_notification_threw',
	/** One or more per-property hydration promises rejected inside a user-search batch. Per-item failures never abort the batch, so this line is the only signal that a result set is short. */
	PROPERTY_FETCH_BATCH_FAILURES: 'property_fetch_batch_failures',
	/** The mission viewer's reader pins could not be read from or written to the browser's storage (a locked-down
	 *  browser, quota, privacy mode): the page keeps its in-memory pins and this line is the only trace (2026-09-13). */
	MISSION_VIEWER_PIN_STORE_REFUSED: 'mission-viewer: the browser refused the pins store',
} as const;

export const LOG_LEVELS = { DEBUG: 'debug', INFO: 'info', WARN: 'warn', ERROR: 'error' } as const;

/**
 * localStorage key that turns the BROWSER console's debug and info lines back on (`'1'`). Off, the unconfigured
 * console fallback (`utils/deps/registry.ts`) prints only warnings and errors in a browser: the socket lifecycle,
 * request and reconnect diagnostics every surface emits at debug were filling the operator's console on every page
 * load (operator directive 2026-09-02). Workers and Node keep every level; only a document is quiet by default.
 */
export const BROWSER_DEBUG_LOG_STORAGE_KEY = 'federa_debug_logs';

/** A scan-output row's severity. Which of them BLOCKS is not decided here: the one error tier is `isErrorSeverity`
 *  (`const/severity.ts`), where ERROR reads blocking and WARNING and MEDIUM do not. */
export const SCAN_OUTPUT_SEVERITY = { ERROR: 'error', WARNING: 'warning', MEDIUM: 'medium' } as const;

/** @runtime-derived-ssot-helper (const-leaf-pinned): the log context a FAILED flow run writes -- its `error` as
 *  `message`, blank when the envelope omitted one (the member is best-effort on a failure envelope). */
export function failedRunLogContext(run: { readonly error?: string }): { message: string } {
	return { message: run.error ?? '' };
}

/** Scan-issue `source` discriminator values for the unified scan report, shared by the producer collectors and the consumer summary comparisons so they cannot drift, mirroring the `RawScanIssue.source` union. */
export const SCAN_ISSUE_SOURCE = {
	COMPLIANCE: 'compliance',
	ESLINT: 'eslint',
	TYPECHECK: 'typecheck',
	FORMAT: 'format',
	GOVERNANCE: 'governance',
} as const;

/** The FORMAT-source scan-issue vocabulary. The format phase is READ-ONLY as of the 2026-08-26 panel close: it runs
 * `prettier --list-different` and never `--write`, so an unformatted file is an UNCURED finding rather than a defect
 * the phase already fixed. That is why this family now carries an unformatted-file message alongside the crash lead --
 * formatting is enforced by REPORT, not by rewrite. The unformatted rows are gated on the operator drain stamp
 * (`FORMAT_DRAIN_STAMP`, `const/format-drain-stamp.ts`) so the arm is born at zero queue rather than as a standing
 * known-violation tax. */
export const SCAN_FORMAT = {
	/** Rule id stamped on every FORMAT-source scan issue. */
	RULE_ID: 'prettier',
	/** Message lead for the prettier-FAILED scan issue. Reports the UNKNOWN state -- prettier could not run to completion -- which is distinct from a file it read and found unformatted. */
	FAILED_MESSAGE_LEAD: 'prettier FAILED, formatting state unknown: ',
	/** Message on each per-file row once the rows arm is stamped. States the cure explicitly, because the scan no longer performs it and a reader who remembers the write arm would otherwise wait for a fix that never comes. */
	UNFORMATTED_MESSAGE:
		'file is not prettier-formatted. The scan is READ-ONLY and did not rewrite it -- run the `format-write` flow over the file to cure.',
	/** Message lead for a dormant envelope that carried no dormancy sentence of its own; the declined count is appended. This is a PRODUCER defect (the format node owes the note) reported rather than swallowed, since dropping it would read as a clean format phase. */
	ROWS_DORMANT_UNSTATED_LEAD:
		'format-rows arm reported DORMANT but supplied no dormancy note -- the format node did not compose one, so this run DECLINED to file rows and cannot say why. Never read this as clean formatting. Declined file count: ',
	/** Message on a row for a file prettier could not PARSE (`[error] <path>: SyntaxError`). Distinct from unformatted: the
	 *  file is not the language its suffix claims (witnessed 2026-09-01: a JSONL ledger wearing `.json`), and until it is
	 *  the whole batch it rode in would fail and leave every later file unchecked, so it is contained to its own row. */
	UNPARSABLE_MESSAGE:
		'prettier could not parse this file: its contents are not the language its suffix declares. Rename the suffix or fix the contents; this row is not a formatting drift and `format-write` cannot cure it.',
	/** Synthetic anchor path for a prettier failure or a dormancy notice that names no per-file path: dropping it would hide a genuinely uncured or declined format state. */
	FAILURE_ANCHOR_PATH: '(prettier)',
} as const;

/** The COMPLIANCE-source scan-issue vocabulary for findings that belong to the RUN rather than to any one file, minted for the unreadable-violations-store row. */
export const SCAN_COMPLIANCE = {
	/** Synthetic anchor path for the store-unreadable row: it is a finding about the RUN, and scoping it away would restore the false green it exists to close. */
	ANCHOR_PATH: '(compliance)',
	/** Message lead for the store-unreadable row; the read/parse cause is appended by the caller. */
	STORE_UNREADABLE_MESSAGE_LEAD:
		'this run could not READ its compliance violations store, so every compliance count it reports is a measurement of NOTHING rather than a clean tree -- the store is gitignored build output written by rule-flush-writes, so an absent or half-written file means the rule lane produced no readable verdict. Never read this as a pass. Cause: ',
} as const;
